import { create } from 'zustand';

export interface AppError {
  id: string;
  timestamp: string;
  code: string;
  severity: 'fatal' | 'error' | 'warning' | 'info';
  source: string;
  message: string;
  context?: Record<string, unknown>;
}

interface ErrorState {
  errors: AppError[];
  addError: (error: Omit<AppError, 'id' | 'timestamp'>) => void;
  removeError: (id: string) => void;
  clearErrors: () => void;
  getRecentErrors: (limit?: number) => AppError[];
}

const MAX_ERRORS = 100;

const sanitizeContext = (ctx?: Record<string, unknown>): Record<string, unknown> | undefined => {
  if (!ctx) return undefined;
  const sensitiveKeys = ['password', 'token', 'secret', 'apiKey', 'credential', 'authorization'];
  const sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(ctx)) {
    if (sensitiveKeys.some((s) => key.toLowerCase().includes(s))) {
      sanitized[key] = '***';
    } else {
      sanitized[key] = value;
    }
  }
  return sanitized;
};

export const useErrorStore = create<ErrorState>()((set, get) => ({
  errors: [],
  addError: (error) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const entry: AppError = {
      ...error,
      id,
      timestamp: new Date().toISOString(),
      context: sanitizeContext(error.context),
    };
    set((state) => {
      const next = [entry, ...state.errors];
      if (next.length > MAX_ERRORS) next.length = MAX_ERRORS;
      return { errors: next };
    });
  },
  removeError: (id) => {
    set((state) => ({ errors: state.errors.filter((e) => e.id !== id) }));
  },
  clearErrors: () => set({ errors: [] }),
  getRecentErrors: (limit = 100) => get().errors.slice(0, limit),
}));

export const addError = (error: Omit<AppError, 'id' | 'timestamp'>): void => {
  useErrorStore.getState().addError(error);
};
